// src/client/game/core/enemy/controllers/ForageController.ts
import type { EnemyController } from "../EnemyController";
import type { EnemyContext } from "../EnemyContext";
import { FoodKind, type Direction, type FoodInstance, type GridPos } from "../../types";

/**
 * "Legacy" forage controller: matches the existing EnemySystem behavior.
 * - Chases Gold if any exist, else Red.
 * - Uses BFS to find a safe shortest path around obstacles/snakes.
 * - Falls back to a greedy safe step when no path is found.
 */
export class ForageController implements EnemyController {
  readonly id = "forage";

  decideNextMove(ctx: EnemyContext): Direction {
    const { width, height } = ctx.world;
    const enemy = ctx.self.snake;
    const head = enemy.head;

    const targetFoods = this.pickTargetFoods(ctx.world.foods);
    if (targetFoods.length === 0) return enemy.direction;

    const startIndex = head.y * width + head.x;
    const foodSet = new Set<number>();
    for (const f of targetFoods) foodSet.add(f.y * width + f.x);

    const blocked = this.buildBlockedSet(ctx, startIndex);

    const next = this.bfsNextStep(width, height, startIndex, foodSet, blocked);
    if (next !== null) {
      const dx = (next % width) - head.x;
      const dy = Math.floor(next / width) - head.y;
      if (dx === 1 && dy === 0) return { dx: 1, dy: 0 };
      if (dx === -1 && dy === 0) return { dx: -1, dy: 0 };
      if (dx === 0 && dy === 1) return { dx: 0, dy: 1 };
      if (dx === 0 && dy === -1) return { dx: 0, dy: -1 };
    }

    const greedy = this.chooseGreedyDirection(ctx, targetFoods);
    return greedy ?? enemy.direction;
  }

  private pickTargetFoods(foods: FoodInstance[]): FoodInstance[] {
    const gold = foods.filter((f) => f.id === FoodKind.Gold);
    if (gold.length > 0) return gold;
    const red = foods.filter((f) => f.id === FoodKind.Normal);
    return red;
  }

  private buildBlockedSet(ctx: EnemyContext, startIndex: number): Set<number> {
    const { width, height, obstacles, player, enemies } = ctx.world;
    const blocked = new Set<number>();

    // Obstacles
    for (const key of obstacles) {
      const [sx, sy] = key.split(",").map((n) => parseInt(n, 10));
      if (Number.isFinite(sx) && Number.isFinite(sy)) {
        blocked.add(sy * width + sx);
      }
    }

    // Player snake segments
    for (const seg of player.segments) {
      blocked.add(seg.y * width + seg.x);
    }

    // All enemy snake segments (including this one)
    for (const e of enemies) {
      for (const seg of e.snake.segments) {
        blocked.add(seg.y * width + seg.x);
      }
    }

    // Allow current head cell
    blocked.delete(startIndex);

    // Defensive bounds: prevent any accidental out-of-bounds indices
    // (BFS checks bounds too, but this keeps the set clean.)
    for (const idx of Array.from(blocked)) {
      if (idx < 0 || idx >= width * height) blocked.delete(idx);
    }

    return blocked;
  }

  private bfsNextStep(
    width: number,
    height: number,
    startIndex: number,
    foodSet: Set<number>,
    blocked: Set<number>
  ): number | null {
    const queue: number[] = [startIndex];
    const visited = new Array<boolean>(width * height).fill(false);
    const prev = new Array<number>(width * height).fill(-1);
    visited[startIndex] = true;

    const dirs: Direction[] = [
      { dx: 1, dy: 0 },
      { dx: -1, dy: 0 },
      { dx: 0, dy: 1 },
      { dx: 0, dy: -1 },
    ];

    let targetIndex = -1;

    while (queue.length > 0) {
      const cur = queue.shift()!;
      if (foodSet.has(cur)) {
        targetIndex = cur;
        break;
      }

      const cx = cur % width;
      const cy = Math.floor(cur / width);

      for (const d of dirs) {
        const nx = cx + d.dx;
        const ny = cy + d.dy;
        if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue;
        const ni = ny * width + nx;
        if (visited[ni] || blocked.has(ni)) continue;
        visited[ni] = true;
        prev[ni] = cur;
        queue.push(ni);
      }
    }

    if (targetIndex === -1) return null;

    // Reconstruct path from target to start, stop at immediate neighbor of start
    let cur = targetIndex;
    let p = prev[cur];
    while (p !== -1 && p !== startIndex) {
      cur = p;
      p = prev[cur];
    }
    return cur;
  }

  private chooseGreedyDirection(
    ctx: EnemyContext,
    targetFoods: FoodInstance[]
  ): Direction | null {
    const { width, height, obstacles, player, enemies } = ctx.world;
    const enemy = ctx.self.snake;
    const head = enemy.head;

    if (targetFoods.length === 0) return null;

    // Closest target food by Manhattan distance
    let bestFood = targetFoods[0];
    let bestDist = this.manhattan(head, bestFood);

    for (const f of targetFoods) {
      const d = this.manhattan(head, f);
      if (d < bestDist) {
        bestDist = d;
        bestFood = f;
      }
    }

    const candidates: Direction[] = [
      { dx: 1, dy: 0 },
      { dx: -1, dy: 0 },
      { dx: 0, dy: 1 },
      { dx: 0, dy: -1 },
    ];

    let bestDir: Direction | null = null;
    let bestStepDist = Infinity;

    for (const dir of candidates) {
      const nx = head.x + dir.dx;
      const ny = head.y + dir.dy;
      if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue;

      const key = `${nx},${ny}`;
      const isBlocked =
        obstacles.has(key) ||
        player.segments.some((s) => s.x === nx && s.y === ny) ||
        enemies.some((e) => e.snake.segments.some((s) => s.x === nx && s.y === ny));

      if (isBlocked) continue;

      const dist = Math.abs(bestFood.x - nx) + Math.abs(bestFood.y - ny);
      if (dist < bestStepDist) {
        bestStepDist = dist;
        bestDir = dir;
      }
    }

    return bestDir;
  }

  private manhattan(a: GridPos, b: GridPos): number {
    return Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
  }
}
